import React from "react";
import type { PaymentItem } from "../lib/billing-api.js";
import { CheckCircle2, AlertCircle, Clock } from "lucide-react";

export interface PaymentHistoryListProps {
  payments: PaymentItem[];
  isLoading?: boolean;
}

export function PaymentHistoryList({
  payments,
  isLoading,
}: PaymentHistoryListProps): React.ReactElement {
  if (isLoading) {
    return (
      <div
        style={{
          backgroundColor: "var(--color-paper)",
          borderRadius: "var(--radius-card)",
          boxShadow: "var(--shadow-floating)",
          padding: "16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          flex: 1,
          minHeight: 0,
        }}
      >
        {[1, 2, 3].map((k) => (
          <div
            key={k}
            style={{
              height: "48px",
              backgroundColor: "var(--color-canvas)",
              borderRadius: "var(--radius-input)",
            }}
          />
        ))}
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <div
        data-testid="empty-payments-state"
        style={{
          backgroundColor: "var(--color-paper)",
          borderRadius: "var(--radius-card)",
          boxShadow: "var(--shadow-floating)",
          padding: "28px 20px",
          textAlign: "center",
          color: "var(--color-slate)",
          fontSize: "14px",
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        История платежей пуста
      </div>
    );
  }

  return (
    <div
      data-testid="payments-history-list"
      style={{
        backgroundColor: "var(--color-paper)",
        borderRadius: "var(--radius-card)",
        boxShadow: "var(--shadow-floating)",
        padding: "8px 20px",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {payments.map((payment, index) => {
        const isLast = index === payments.length - 1;
        const formattedDate = new Date(payment.date).toLocaleDateString("ru-RU", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });

        const isSuccess =
          payment.status.toUpperCase() === "SUCCEEDED" ||
          payment.status.toUpperCase() === "PAID" ||
          payment.status.toUpperCase() === "COMPLETED";

        return (
          <div
            key={payment.id}
            data-testid={`payment-item-${payment.id}`}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 0",
              borderBottom: isLast ? "none" : "1px solid var(--color-frost-border)",
              flexShrink: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  color: isSuccess ? "var(--color-success)" : "var(--color-slate)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {isSuccess ? (
                  <CheckCircle2 size={18} />
                ) : payment.status.toLowerCase() === "pending" ? (
                  <Clock size={18} />
                ) : (
                  <AlertCircle size={18} />
                )}
              </div>
              <div>
                <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-graphite)" }}>
                  {payment.amount} {payment.currency.toUpperCase()}
                </div>
                <div style={{ fontSize: "12px", color: "var(--color-slate)" }}>{formattedDate}</div>
              </div>
            </div>

            <span
              style={{
                fontSize: "13px",
                fontWeight: 500,
                color: isSuccess ? "var(--color-success)" : "var(--color-slate)",
                flexShrink: 0,
              }}
            >
              {isSuccess ? "Оплачено" : payment.status}
            </span>
          </div>
        );
      })}
    </div>
  );
}
